import React, { useEffect, useState, useCallback } from 'react';
import { Settings as SettingsIcon, CreditCard, Save, Plus, Trash2, Check, X, Package, Phone, Mail } from 'lucide-react';
import api from '../api/client';
import toast from 'react-hot-toast';

// ─── Service definitions for the plan editor ───
const SERVICE_DEFS = [
  { key: 'ai_rag', label: 'AI Queries / month', hasLimit: true },
  { key: 'ai_rag_deep', label: 'Deep Analyses / month', hasLimit: true },
  { key: 'email', label: 'Email sends / month', hasLimit: true },
  { key: 'sms', label: 'SMS messages / month', hasLimit: true },
  { key: 'voice', label: 'Voice minutes / month', hasLimit: true },
  { key: 'textract', label: 'OCR pages / month', hasLimit: true },
  { key: 'storage', label: 'Storage (MB)', hasLimit: true },
  { key: 'document_generation', label: 'Document generation', hasLimit: false },
];

// Default seed values when migrating from old boolean format
const PLAN_DEFAULTS = {
  free: { ai_rag: 20, ai_rag_deep: 2, email: 100, sms: 0, voice: 0, textract: 0, storage: 100, document_generation: false },
  starter: { ai_rag: 200, ai_rag_deep: 10, email: 1000, sms: 500, voice: 100, textract: 0, storage: 5120, document_generation: true },
  professional: { ai_rag: 500, ai_rag_deep: 50, email: 5000, sms: 2000, voice: 500, textract: 200, storage: 25600, document_generation: true },
  enterprise: { ai_rag: null, ai_rag_deep: null, email: null, sms: null, voice: null, textract: null, storage: null, document_generation: true },
};

/**
 * Normalize a plan's included_services to the new object format.
 * Handles: boolean values, missing keys, legacy monthly_limit_mb.
 */
function normalizeIncludedServices(svc, planName) {
  const defaults = PLAN_DEFAULTS[planName] || PLAN_DEFAULTS.free;
  const result = {};
  for (const def of SERVICE_DEFS) {
    const val = svc?.[def.key];
    if (val === true) {
      result[def.key] = def.hasLimit
        ? { enabled: true, monthly_limit: defaults[def.key] ?? null }
        : { enabled: true };
    } else if (val === false || val === undefined || val === null) {
      if (val === undefined && defaults[def.key] !== undefined) {
        // Key missing entirely — seed from defaults
        const defVal = defaults[def.key];
        if (!def.hasLimit) {
          result[def.key] = { enabled: !!defVal };
        } else {
          result[def.key] = { enabled: defVal !== 0 && defVal !== false, monthly_limit: defVal === false ? 0 : defVal };
        }
      } else {
        result[def.key] = def.hasLimit ? { enabled: false, monthly_limit: 0 } : { enabled: false };
      }
    } else if (typeof val === 'object') {
      if (!def.hasLimit) {
        result[def.key] = { enabled: val.enabled !== false };
      } else {
        const limit = val.monthly_limit !== undefined ? val.monthly_limit : (val.monthly_limit_mb !== undefined ? val.monthly_limit_mb : null);
        result[def.key] = { enabled: val.enabled !== false, monthly_limit: limit };
      }
    }
  }
  return result;
}

export default function Settings() {
  const [stripeConfig, setStripeConfig] = useState(null);
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAddons, setSavingAddons] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editingAddon, setEditingAddon] = useState(null);
  const [commProvider, setCommProvider] = useState('mock');
  const [commConfig, setCommConfig] = useState({ apiKey: '', apiSecret: '', applicationId: '', fromNumber: '' });
  const [commTestResult, setCommTestResult] = useState(null);
  const [testingComm, setTestingComm] = useState(false);
  const [savingComm, setSavingComm] = useState(false);
  const [emailProvider, setEmailProvider] = useState('mock');
  const [emailConfig, setEmailConfig] = useState({ apiKey: '', fromEmail: '', fromName: '' });
  const [emailTestResult, setEmailTestResult] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const [configRes, addonsRes, servicesRes] = await Promise.all([
        api.get('/platform/stripe/config'),
        api.get('/platform/addons').catch(() => ({ data: { addons: [] } })),
        api.get('/platform/services').catch(() => ({ data: { services: [] } })),
      ]);
      setStripeConfig(configRes.data);
      setPlans(configRes.data.plans || []);
      setAddons(addonsRes.data?.addons || []);

      // Load communication provider config from SMS service
      const smsSvc = (servicesRes.data?.services || []).find(s => s.slug === 'sms');
      if (smsSvc) {
        setCommProvider((smsSvc.current_provider || 'mock').toLowerCase());
        setCommConfig(smsSvc.provider_config || { apiKey: '', apiSecret: '', applicationId: '', fromNumber: '' });
      }

      // Load email provider config
      const emailSvc = (servicesRes.data?.services || []).find(s => s.slug === 'email');
      if (emailSvc) {
        setEmailProvider((emailSvc.current_provider || 'mock').toLowerCase());
        setEmailConfig(emailSvc.provider_config || { apiKey: '', fromEmail: '', fromName: '' });
      }
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const savePlans = async () => {
    setSaving(true);
    try {
      await api.put('/platform/stripe/config', { plans });
      toast.success('Subscription plans saved');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addPlan = () => {
    const newPlan = {
      name: `plan_${Date.now()}`,
      display_name: '',
      monthly_price: 0,
      stripe_price_id: '',
      max_users: null,
      max_records: null,
      portal_branding: null,
      included_services: normalizeIncludedServices({}, 'free'),
    };
    setPlans([...plans, newPlan]);
    setEditingPlan(plans.length);
  };

  const removePlan = (idx) => {
    setPlans(plans.filter((_, i) => i !== idx));
    setEditingPlan(null);
  };

  const saveAddons = async () => {
    setSavingAddons(true);
    try {
      await api.put('/platform/addons', { addons });
      toast.success('Add-on definitions saved');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSavingAddons(false);
    }
  };

  const addAddon = () => {
    const newAddon = { service: '', display_name: '', price: 0, stripe_price_id: '', included_quantity: 0, unit: '' };
    setAddons([...addons, newAddon]);
    setEditingAddon(addons.length);
  };

  const removeAddon = (idx) => {
    setAddons(addons.filter((_, i) => i !== idx));
    setEditingAddon(null);
  };

  const updateAddon = (idx, field, value) => {
    const updated = [...addons];
    updated[idx] = { ...updated[idx], [field]: value };
    setAddons(updated);
  };

  const testCommProvider = async () => {
    setTestingComm(true);
    setCommTestResult(null);
    try {
      const res = await api.post('/platform/services/sms/test-provider');
      setCommTestResult(res.data);
    } catch (err) {
      setCommTestResult({ success: false, error: err.response?.data?.error || err.message });
    } finally {
      setTestingComm(false);
    }
  };

  const saveCommProvider = async () => {
    setSavingComm(true);
    try {
      await api.put('/platform/services/sms/provider-config', {
        provider: commProvider,
        config: (commProvider === 'vonage' || commProvider === 'twilio') ? commConfig : {},
      });
      toast.success('Communication provider saved');
      setCommTestResult(null);
    } catch (err) {
      toast.error('Failed to save provider config');
    } finally {
      setSavingComm(false);
    }
  };

  const testEmailProvider = async () => {
    setTestingEmail(true);
    setEmailTestResult(null);
    try {
      const res = await api.post('/platform/services/email/test-provider');
      setEmailTestResult(res.data);
    } catch (err) {
      setEmailTestResult({ success: false, error: err.response?.data?.error || err.message });
    } finally {
      setTestingEmail(false);
    }
  };

  const saveEmailProvider = async () => {
    setSavingEmail(true);
    try {
      await api.put('/platform/services/email/provider-config', {
        provider: emailProvider,
        config: emailProvider === 'brevo' ? emailConfig : {},
      });
      toast.success('Email provider saved');
      setEmailTestResult(null);
    } catch (err) {
      toast.error('Failed to save email provider config');
    } finally {
      setSavingEmail(false);
    }
  };

  const updatePlan = (idx, field, value) => {
    const updated = [...plans];
    updated[idx] = { ...updated[idx], [field]: value };
    setPlans(updated);
  };

  const updateService = (planIdx, serviceKey, field, value) => {
    const updated = [...plans];
    const plan = { ...updated[planIdx] };
    const svc = { ...(plan.included_services || {}) };
    svc[serviceKey] = { ...(svc[serviceKey] || {}), [field]: value };
    plan.included_services = svc;
    updated[planIdx] = plan;
    setPlans(updated);
  };

  // Normalize plans on load — ensure included_services uses new format
  const normalizedRef = React.useRef(false);
  if (plans.length > 0 && !normalizedRef.current) {
    let needsNormalize = false;
    for (const p of plans) {
      const svc = p.included_services || {};
      for (const def of SERVICE_DEFS) {
        const val = svc[def.key];
        if (val === true || val === false || val === undefined) { needsNormalize = true; break; }
        if (typeof val === 'object' && def.hasLimit && val.monthly_limit === undefined && val.monthly_limit_mb !== undefined) { needsNormalize = true; break; }
      }
      if (needsNormalize) break;
    }
    if (needsNormalize) {
      // Schedule normalization for next render to avoid setState during render issues
      setTimeout(() => {
        setPlans(prev => prev.map(p => ({
          ...p,
          included_services: normalizeIncludedServices(p.included_services, p.name),
        })));
      }, 0);
    }
    normalizedRef.current = true;
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-gray-200 rounded" />
          <div className="h-64 bg-gray-200 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center gap-3 mb-6">
        <SettingsIcon className="w-6 h-6 text-gray-400" />
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
      </div>

      {/* Stripe Status */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard className="w-5 h-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Stripe Integration</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-500">Status</p>
            <div className="mt-1 flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${stripeConfig?.configured ? 'bg-green-500' : 'bg-gray-400'}`} />
              <span className="text-sm font-medium text-gray-900">
                {stripeConfig?.configured ? 'Connected' : 'Not Configured'}
              </span>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-500">Mode</p>
            <p className="mt-1 text-sm font-medium text-gray-900">
              {stripeConfig?.config?.mode === 'live' ? 'Live' : 'Test'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Publishable Key</p>
            <p className="mt-1 text-sm font-mono text-gray-700 truncate">
              {stripeConfig?.publishable_key ? `${stripeConfig.publishable_key.slice(0, 20)}...` : 'Not set'}
            </p>
          </div>
        </div>
        {!stripeConfig?.configured && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            Stripe is not configured. Set STRIPE_SECRET_KEY, STRIPE_WEBHOOK_SECRET, and STRIPE_PUBLISHABLE_KEY in environment variables.
            Billing operates in manual mode until Stripe is configured.
          </div>
        )}
      </div>

      {/* Subscription Plans */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Subscription Plans</h2>
            <p className="text-sm text-gray-500">Configure available subscription tiers for tenants</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={addPlan}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-brand-600 bg-brand-50 rounded-lg hover:bg-brand-100">
              <Plus className="w-4 h-4" /> Add Plan
            </button>
            <button onClick={savePlans} disabled={saving}
              className="flex items-center gap-1 px-4 py-1.5 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50">
              <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Plans'}
            </button>
          </div>
        </div>

        <div className="p-6">
          {plans.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No subscription plans configured</p>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {plans.map((plan, idx) => {
                const svc = plan.included_services || {};
                return (
                  <div key={plan.name} className={`border rounded-xl p-4 ${editingPlan === idx ? 'border-brand-500 ring-1 ring-brand-500' : 'border-gray-200'}`}>
                    {editingPlan === idx ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-gray-500">Slug</label>
                            <input type="text" value={plan.name} onChange={e => updatePlan(idx, 'name', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Display Name</label>
                            <input type="text" value={plan.display_name} onChange={e => updatePlan(idx, 'display_name', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-xs text-gray-500">$/mo</label>
                            <input type="number" value={plan.monthly_price} onChange={e => updatePlan(idx, 'monthly_price', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Max Users</label>
                            <input type="number" value={plan.max_users ?? ''} placeholder="∞"
                              onChange={e => updatePlan(idx, 'max_users', e.target.value === '' ? null : parseInt(e.target.value))}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Max Records</label>
                            <input type="number" value={plan.max_records ?? ''} placeholder="∞"
                              onChange={e => updatePlan(idx, 'max_records', e.target.value === '' ? null : parseInt(e.target.value))}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
                          </div>
                        </div>

                        {/* Service Limits Table */}
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-600 border-b border-gray-200">
                            Service Limits
                          </div>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-gray-100 bg-gray-50/50">
                                <th className="text-left px-3 py-1.5 font-medium text-gray-500">Service</th>
                                <th className="text-center px-2 py-1.5 font-medium text-gray-500 w-16">On</th>
                                <th className="text-right px-3 py-1.5 font-medium text-gray-500 w-24">Limit</th>
                                <th className="text-center px-2 py-1.5 font-medium text-gray-500 w-16">∞</th>
                              </tr>
                            </thead>
                            <tbody>
                              {SERVICE_DEFS.map(def => {
                                const val = svc[def.key] || {};
                                const enabled = typeof val === 'object' ? val.enabled !== false : !!val;
                                const limit = typeof val === 'object' ? val.monthly_limit : null;
                                const isUnlimited = limit === null || limit === undefined;
                                return (
                                  <tr key={def.key} className="border-b border-gray-50">
                                    <td className={`px-3 py-1.5 ${enabled ? 'text-gray-700' : 'text-gray-400'}`}>{def.label}</td>
                                    <td className="text-center px-2 py-1.5">
                                      <input type="checkbox" checked={enabled}
                                        onChange={e => {
                                          updateService(idx, def.key, 'enabled', e.target.checked);
                                          if (!e.target.checked && def.hasLimit) {
                                            updateService(idx, def.key, 'monthly_limit', 0);
                                          }
                                        }}
                                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                                    </td>
                                    {def.hasLimit ? (
                                      <>
                                        <td className="text-right px-3 py-1.5">
                                          <input type="number" min="0"
                                            value={isUnlimited ? '' : (limit ?? '')}
                                            placeholder={isUnlimited ? '∞' : '0'}
                                            disabled={!enabled || isUnlimited}
                                            onChange={e => updateService(idx, def.key, 'monthly_limit', e.target.value === '' ? 0 : parseInt(e.target.value))}
                                            className="w-full px-1.5 py-0.5 border border-gray-300 rounded text-xs text-right focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-gray-100 disabled:text-gray-400" />
                                        </td>
                                        <td className="text-center px-2 py-1.5">
                                          <input type="checkbox" checked={enabled && isUnlimited}
                                            disabled={!enabled}
                                            onChange={e => updateService(idx, def.key, 'monthly_limit', e.target.checked ? null : 0)}
                                            className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 disabled:opacity-40" />
                                        </td>
                                      </>
                                    ) : (
                                      <>
                                        <td className="text-right px-3 py-1.5 text-gray-400">—</td>
                                        <td className="text-center px-2 py-1.5 text-gray-400">—</td>
                                      </>
                                    )}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        <div className="flex gap-2">
                          <button onClick={() => setEditingPlan(null)} className="flex items-center gap-1 px-2 py-1 text-xs text-green-700 bg-green-50 rounded hover:bg-green-100">
                            <Check className="w-3 h-3" /> Done
                          </button>
                          <button onClick={() => removePlan(idx)} className="flex items-center gap-1 px-2 py-1 text-xs text-red-700 bg-red-50 rounded hover:bg-red-100">
                            <Trash2 className="w-3 h-3" /> Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div onClick={() => setEditingPlan(idx)} className="cursor-pointer">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-semibold text-gray-900">{plan.display_name || plan.name || 'Untitled'}</h3>
                          <span className="text-xs text-gray-400 font-mono">{plan.name}</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900 mb-2">
                          {plan.monthly_price === 0 ? 'Free' : `$${plan.monthly_price}`}
                          {plan.monthly_price > 0 && <span className="text-sm font-normal text-gray-500">/mo</span>}
                        </p>
                        <div className="text-xs text-gray-500 space-y-0.5 mb-2">
                          <p>Users: {plan.max_users ?? '∞'} | Records: {plan.max_records ?? '∞'}</p>
                        </div>
                        <div className="space-y-0.5">
                          {SERVICE_DEFS.map(def => {
                            const val = svc[def.key];
                            const enabled = val && typeof val === 'object' ? val.enabled !== false : !!val;
                            const limit = val && typeof val === 'object' ? val.monthly_limit : null;
                            return (
                              <div key={def.key} className={`flex items-center gap-1.5 text-xs ${enabled ? 'text-gray-700' : 'text-gray-400'}`}>
                                {enabled ? <Check className="w-3 h-3 text-green-500 flex-shrink-0" /> : <X className="w-3 h-3 flex-shrink-0" />}
                                <span>{def.key}</span>
                                {enabled && def.hasLimit && (
                                  <span className="text-gray-400">
                                    {limit === null || limit === undefined ? '(∞)' : `(${Number(limit).toLocaleString()})`}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Communication Provider (SMS + Voice) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Phone className="w-5 h-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Communication Provider (SMS + Voice)</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
            <select
              value={commProvider}
              onChange={e => { setCommProvider(e.target.value); setCommTestResult(null); }}
              className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              <option value="mock">Mock (Development)</option>
              <option value="vonage">Vonage</option>
              <option value="twilio">Twilio</option>
            </select>
          </div>

          {commProvider === 'vonage' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <label className="block text-xs text-gray-500 mb-1">API Key</label>
                <input type="text" value={commConfig.apiKey || ''}
                  onChange={e => setCommConfig({ ...commConfig, apiKey: e.target.value })}
                  placeholder="Enter Vonage API Key"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">API Secret</label>
                <input type="password" value={commConfig.apiSecret || ''}
                  onChange={e => setCommConfig({ ...commConfig, apiSecret: e.target.value })}
                  placeholder="Enter Vonage API Secret"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Application ID</label>
                <input type="text" value={commConfig.applicationId || ''}
                  onChange={e => setCommConfig({ ...commConfig, applicationId: e.target.value })}
                  placeholder="Optional — for voice calls"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">From Number</label>
                <input type="text" value={commConfig.fromNumber || ''}
                  onChange={e => setCommConfig({ ...commConfig, fromNumber: e.target.value })}
                  placeholder="e.g. 15551234567"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </div>
            </div>
          )}

          {commProvider === 'twilio' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Account SID</label>
                <input type="text" value={commConfig.account_sid || ''}
                  onChange={e => setCommConfig({ ...commConfig, account_sid: e.target.value })}
                  placeholder="ACxxxxxxxxxxxx"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Auth Token</label>
                <input type="password" value={commConfig.auth_token || ''}
                  onChange={e => setCommConfig({ ...commConfig, auth_token: e.target.value })}
                  placeholder="Enter Auth Token"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">From Number</label>
                <input type="text" value={commConfig.from_number || ''}
                  onChange={e => setCommConfig({ ...commConfig, from_number: e.target.value })}
                  placeholder="+18058008009"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Messaging Service SID</label>
                <input type="text" value={commConfig.messaging_service_sid || ''}
                  onChange={e => setCommConfig({ ...commConfig, messaging_service_sid: e.target.value })}
                  placeholder="MGxxxxxxxxxxxx"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </div>
            </div>
          )}
          {commTestResult && (
            <div className={`p-3 rounded-lg text-sm ${commTestResult.success ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
              {commTestResult.success
                ? commProvider === 'twilio' ? `Connected! Account: ${commTestResult.accountName} (${commTestResult.status})` : `Connected! Balance: ${commTestResult.balance} ${commTestResult.currency}`
                : `Failed: ${commTestResult.error}`}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button onClick={testCommProvider} disabled={testingComm}
              className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
              {testingComm ? 'Testing...' : 'Test Connection'}
            </button>
            <button onClick={saveCommProvider} disabled={savingComm}
              className="flex items-center gap-1 px-4 py-2 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50">
              <Save className="w-4 h-4" /> {savingComm ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Email Provider */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Mail className="w-5 h-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Email Provider</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
            <select
              value={emailProvider}
              onChange={e => { setEmailProvider(e.target.value); setEmailTestResult(null); }}
              className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              <option value="mock">Mock (Development)</option>
              <option value="brevo">Brevo</option>
            </select>
          </div>

          {emailProvider === 'brevo' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <label className="block text-xs text-gray-500 mb-1">API Key</label>
                <input type="password" value={emailConfig.apiKey || ''}
                  onChange={e => setEmailConfig({ ...emailConfig, apiKey: e.target.value })}
                  placeholder="xkeysib-..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">From Email</label>
                <input type="email" value={emailConfig.fromEmail || ''}
                  onChange={e => setEmailConfig({ ...emailConfig, fromEmail: e.target.value })}
                  placeholder="admin@sixty10.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">From Name</label>
                <input type="text" value={emailConfig.fromName || ''}
                  onChange={e => setEmailConfig({ ...emailConfig, fromName: e.target.value })}
                  placeholder="Sixty10"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </div>
            </div>
          )}

          {emailTestResult && (
            <div className={`p-3 rounded-lg text-sm ${emailTestResult.success ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
              {emailTestResult.success
                ? `Connected! Account: ${emailTestResult.details?.email || 'OK'} — Plan: ${emailTestResult.details?.plan || 'N/A'} — Credits: ${emailTestResult.details?.credits ?? 'N/A'}`
                : `Failed: ${emailTestResult.error}`}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button onClick={testEmailProvider} disabled={testingEmail}
              className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
              {testingEmail ? 'Testing...' : 'Test Connection'}
            </button>
            <button onClick={saveEmailProvider} disabled={savingEmail}
              className="flex items-center gap-1 px-4 py-2 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50">
              <Save className="w-4 h-4" /> {savingEmail ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Service Add-ons */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-6">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <Package className="w-5 h-5 text-gray-400" />
              <h2 className="text-lg font-semibold text-gray-900">Service Add-ons</h2>
            </div>
            <p className="text-sm text-gray-500 mt-1">À la carte service packs that can be assigned to individual tenants</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={addAddon}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-brand-600 bg-brand-50 rounded-lg hover:bg-brand-100">
              <Plus className="w-4 h-4" /> Add
            </button>
            <button onClick={saveAddons} disabled={savingAddons}
              className="flex items-center gap-1 px-4 py-1.5 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50">
              <Save className="w-4 h-4" /> {savingAddons ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
        <div className="p-6">
          {addons.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No add-ons configured</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Service Slug</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Display Name</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-600">Price</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-600">Included Qty</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Unit</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-600"></th>
                </tr>
              </thead>
              <tbody>
                {addons.map((addon, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    {editingAddon === idx ? (
                      <>
                        <td className="px-3 py-2">
                          <input type="text" value={addon.service} onChange={e => updateAddon(idx, 'service', e.target.value)}
                            placeholder="e.g. sms" className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="text" value={addon.display_name} onChange={e => updateAddon(idx, 'display_name', e.target.value)}
                            placeholder="SMS Pack" className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" value={addon.price} onChange={e => updateAddon(idx, 'price', parseFloat(e.target.value) || 0)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-brand-500" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" value={addon.included_quantity} onChange={e => updateAddon(idx, 'included_quantity', parseInt(e.target.value) || 0)}
                            className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-brand-500" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="text" value={addon.unit} onChange={e => updateAddon(idx, 'unit', e.target.value)}
                            placeholder="messages" className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center gap-1 justify-end">
                            <button onClick={() => setEditingAddon(null)} className="px-2 py-1 text-xs text-green-700 bg-green-50 rounded hover:bg-green-100">
                              <Check className="w-3 h-3" />
                            </button>
                            <button onClick={() => removeAddon(idx)} className="px-2 py-1 text-xs text-red-700 bg-red-50 rounded hover:bg-red-100">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2 font-mono text-gray-700 cursor-pointer" onClick={() => setEditingAddon(idx)}>{addon.service}</td>
                        <td className="px-3 py-2 font-medium text-gray-900 cursor-pointer" onClick={() => setEditingAddon(idx)}>{addon.display_name}</td>
                        <td className="px-3 py-2 text-right text-gray-900 cursor-pointer" onClick={() => setEditingAddon(idx)}>${addon.price}/mo</td>
                        <td className="px-3 py-2 text-right text-gray-700 cursor-pointer" onClick={() => setEditingAddon(idx)}>{addon.included_quantity?.toLocaleString()}</td>
                        <td className="px-3 py-2 text-gray-500 cursor-pointer" onClick={() => setEditingAddon(idx)}>{addon.unit}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => removeAddon(idx)} className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
